import { httpClient } from './http-client';

/**
 * Investment plan, nightly check and recommendations (issue #31).
 *
 * RECOMMENDATIONS ONLY: nothing here, or on the server, places a trade. Every
 * response that can carry a recommendation also carries `disclaimer`, which
 * the UI shows verbatim.
 */
export type AssetClass = 'equity' | 'fixed_income' | 'cash' | 'crypto' | 'commodity' | 'private' | 'other';

export const ASSET_CLASSES: AssetClass[] = ['equity', 'fixed_income', 'cash', 'crypto', 'commodity', 'private', 'other'];

export const CLASS_LABEL: Record<AssetClass, string> = {
  equity: 'Equity',
  fixed_income: 'Fixed income',
  cash: 'Cash',
  crypto: 'Crypto',
  commodity: 'Commodity',
  private: 'Private',
  other: 'Other',
};

export const DISCLAIMER = 'Informational only — not financial advice. FolioWorth never places trades.';

export interface DCAMixLine {
  symbol: string;
  asset_class: AssetClass;
  amount: string | number;
  account_label?: string;
}

export type Cadence = 'trading_daily' | 'weekly' | 'biweekly' | 'monthly';

export interface DCAStream {
  id: string;
  name: string;
  broker: string;
  cadence: Cadence;
  /** Per buy; null = unknown (inferred from history when buys exist). */
  amount: string | number | null;
  /** pct is the share (0-100); amount, when known, is dollars per buy. */
  mix: { symbol?: string; asset_class: AssetClass; pct: string | number; amount?: string | number | null }[];
  account_ids?: string[];
  paused?: boolean;
  start_date?: string;
  preferred?: boolean;
  /** Approximate cadence: remind after this many days without a buy. */
  remind_after_days?: number | null;
}

export interface StreamStatus {
  id: string;
  name: string;
  broker: string;
  cadence: Cadence;
  amount: string | null;
  amount_inferred: boolean;
  daily_pace: string;
  window_start: string;
  occurrences: number;
  hit: number;
  expected: string;
  actual: string;
  missed_dates: string[];
  missed_count: number;
  unverified_dates?: string[];
  verified_through?: string;
  data_stale?: boolean;
  last_buy?: string;
  status: 'on_track' | 'missed' | 'paused' | 'amount_unknown' | 'not_started' | 'no_recent_data' | 'reminder_due';
  remind_after_days?: number;
  days_since_last_buy?: number;
  import_older_than_gap?: boolean;
  note?: string;
}

export interface PaceCheck {
  pool: string;
  target_end: string;
  trading_days_left: number;
  required_daily: string;
  combined_daily: string;
  shortfall_daily: string;
  on_pace: boolean;
  projected_end?: string;
  raise_stream_name?: string;
  raise_to: string;
}

export interface PlanReserve { name: string; amount: string | number; account_id?: string }

export interface PendingTransfer {
  id?: string;
  from_account_id: string;
  to_institution: string;
  currency: string;
  approx_amount?: string | number | null;
  status: 'planned' | 'in_transit' | 'done';
  note?: string;
}

export interface TransferStatus extends PendingTransfer {
  account_name: string;
  estimated_cash_base: string;
  estimated_cash_native: string;
  detected: boolean;
  detected_on?: string;
  effective_status: 'planned' | 'in_transit' | 'done';
}

export interface DCAPlan {
  pool_amount: string | number;
  start_date: string;
  rate: string | number;
  rate_unit: 'trading_day' | 'month';
  target_end_date?: string;
  source?: 'plan' | 'external';
  mix?: DCAMixLine[];
  tolerance_pct?: string | number;
  auto_pool?: boolean;
  streams?: DCAStream[];
}

export interface PlanAccountRule {
  account_id: string;
  include?: boolean;
  asset_class?: AssetClass | '';
  role?: string;
}

export interface InvestmentPlan {
  targets: Partial<Record<AssetClass, string | number>>;
  band_pts: string | number;
  caps: { crypto_pct?: string | number; commodity_pct?: string | number; private_pct?: string | number; single_stock_pct?: string | number };
  cash_floor: string | number;
  reserves?: PlanReserve[];
  pending_transfers?: PendingTransfer[];
  dca?: DCAPlan | null;
  accounts?: PlanAccountRule[];
  watchlist?: string[];
  recommendation_expiry_days: number;
  disable_signals?: boolean;
  enrich_reasons_with_llm?: boolean;
  notes?: string;
}

export interface PlanResponse {
  enabled: boolean;
  version: number;
  plan: InvestmentPlan | null;
  updated_at: string | null;
  disclaimer: string;
}

export interface ClassRow {
  class: AssetClass;
  value: string;
  pct: string;
  target_pct: string;
  drift_pts: string;
  pro_forma_pct: string;
  pro_forma_drift_pts: string;
  status: 'within' | 'over' | 'under' | 'no_target';
}

export interface AllocationAccount {
  id: string;
  name: string;
  institution?: string;
  type: string;
  balance_base: string;
  included: boolean;
  reason: string;
  source?: string;
  by_class?: Partial<Record<AssetClass, string>>;
  role?: string;
  /** plaid-cash | residual-fresh | residual-small | override | account | unpriced */
  cash_source?: string;
  cash?: string;
  unpriced?: string;
}

export interface Allocation {
  total: string;
  classes: ClassRow[];
  cash: {
    value: string; floor: string; reserved: string; below_floor: boolean; shortfall: string;
    target_value: string; planned: string; excess: string;
  };
  accounts: AllocationAccount[];
  positions: { symbol: string; class: AssetClass; value_base: string; weight_pct: string; fund: boolean }[];
  unclassified: string[];
  /** Balance of accounts with no positions yet, left out of the total and drift. */
  unclassified_value?: string;
  cash_by_currency?: { currency: string; native: string; base: string }[];
  unpriced?: { account_id: string; name: string; amount: string; reason: string }[];
  unpriced_value?: string;
  fixed_income?: { sub_class: string; value: string; pct_of_class: string; pct_of_total: string }[];
  options?: { symbol: string; account: string; underlying?: string; expiry?: string; type?: string; strike: string;
    quantity: string; value_base: string; notional: string; expired: boolean }[];
}

export interface DCAProgress {
  configured: boolean;
  source: string;
  schedule_source?: string;
  rate_unit: string;
  rate: string;
  daily_rate: string;
  start_date: string;
  as_of: string;
  trading_days_elapsed: number;
  pool: string;
  expected: string;
  actual: string;
  deviation: string;
  tolerance: string;
  remaining: string;
  status: 'on_track' | 'behind' | 'ahead' | 'complete' | 'not_started' | 'unverified' | '';
  days_off: string;
  projected_end?: string;
  mix: DCAMixLine[];
  auto_pool?: boolean;
  target_end?: string;
  streams?: StreamStatus[];
  pace?: PaceCheck | null;
}

export interface RecSource {
  url?: string;
  title: string;
  source?: string;
  published_at?: string;
}

export interface RecEvent {
  event: string;
  message: string;
  created_at: string;
  quantity?: string;
  amount?: string;
}

export interface Recommendation {
  id: string;
  kind: 'rebalance' | 'dca' | 'signal' | 'cash';
  action: 'BUY' | 'SELL' | 'MOVE' | 'HOLD';
  symbol: string | null;
  asset_class: string | null;
  account_name: string | null;
  quantity: string | null;
  amount: string | null;
  price_at_rec: string | null;
  price_now: string | null;
  outcome_pct: string | null;
  title: string;
  reasons_md: string;
  sources: RecSource[];
  jev_scores: Record<string, unknown>;
  plan_version: number;
  high_conviction: boolean;
  status: 'open' | 'acted' | 'partially_acted' | 'ignored' | 'expired' | 'dismissed';
  followed_qty: string;
  followed_amount: string;
  followed_pct: string;
  dismissed_reason: string | null;
  created_at: string;
  expires_at: string;
  events: RecEvent[];
}

export interface StoredSignal {
  symbol: string;
  choice: 'buy_more' | 'hold' | 'trim' | 'sell' | 'none';
  confidence: string;
  notable: boolean;
  high_conviction: boolean;
  reasons_md: string;
  sources: RecSource[];
  created_at: string;
}

export interface AllocationResponse {
  configured: boolean;
  enabled?: boolean;
  plan_version?: number;
  as_of?: string;
  currency?: string;
  allocation?: Allocation;
  dca?: DCAProgress;
  dca_line?: string;
  open_recommendations?: Recommendation[];
  signals?: StoredSignal[];
  last_check?: {
    run_id: string; created_at: string; trigger: string; recommendations_created?: number;
    risk?: { label: string; target_label: string; vs_target: string; score: number; tiers: TierRow[]; weighted_vol_pct?: number };
  } | null;
  warnings?: string[];
  transfers?: TransferStatus[];
  cap_alerts?: string[];
  disclaimer: string;
}

export type RiskTier = 'core_index' | 'blue_chip' | 'income' | 'growth' | 'speculative';

export const TIER_LABEL: Record<RiskTier, string> = {
  core_index: 'Core index',
  blue_chip: 'Blue chip',
  income: 'Income / split-share',
  growth: 'Growth',
  speculative: 'Speculative',
};

export interface RiskMetrics {
  symbol: string;
  days: number;
  vol_pct?: number;
  beta?: number;
  benchmark?: string;
  max_drawdown_pct?: number;
  from_high_pct?: number;
  last?: number;
  ma50?: number;
  ma200?: number;
  ret_1m_pct?: number;
  ret_3m_pct?: number;
  rel_1m_pct?: number;
  rel_3m_pct?: number;
  largest_gap_pct?: number;
  gap_date?: string;
}

export interface HoldingRisk {
  symbol: string;
  name?: string;
  fund: boolean;
  value_base: string;
  pct_of_equity: string;
  pct_of_investable: string;
  tier: RiskTier;
  tier_reason: string;
  tier_source: 'household' | 'seed' | 'derived';
  metrics: RiskMetrics;
  trend: string;
  trend_line?: string;
  verdict?: { choice: string; confidence: number; high_conviction: boolean; on: string } | null;
}

export interface TierRow {
  tier: RiskTier;
  value: string;
  pct_of_equity: string;
  pct_of_investable: string;
  count: number;
  cap_pct?: string;
  over_cap: boolean;
}

export interface RiskProfile {
  equity_value: string;
  equity_pct_of_investable: string;
  tiers: TierRow[];
  weighted_vol_pct?: number;
  score: number;
  label: string;
  target_label: string;
  vs_target: 'matches' | 'riskier' | 'calmer' | 'unknown';
  top_single_stocks: HoldingRisk[];
  top_single_stocks_pct_of_investable: string;
  over_single_stock_cap: string[];
  alerts: string[];
  holdings: HoldingRisk[];
}

export interface ReminderRule {
  id?: string;
  name: string;
  enabled: boolean;
  kind: string;
  scope: { institution?: string; account_ids?: string[]; symbols?: string[]; asset_class?: AssetClass | ''; side?: 'buy' | 'sell' };
  window_days: number;
  repeat_every_days: number;
  channel: 'email' | 'telegram';
  message_template?: string;
  legacy_stream_id?: string;
  last_event?: { status: string; suppressed: boolean; reason: string; message?: string; fired_at: string } | null;
}

export interface ReminderResult {
  rule_id: string;
  name: string;
  status: 'fired' | 'suppressed' | 'ok' | 'throttled' | 'disabled';
  suppressed: boolean;
  reason: string;
  message?: string;
  days?: number;
  last_buy_date?: string;
  accounts_in_scope: number;
}

export interface RecStats {
  made: number;
  open: number;
  followed: number;
  partial: number;
  ignored: number;
  dismissed: number;
  expired: number;
  follow_through_pct: string;
  money_moved: string;
  avg_followed_pct: string;
  by_kind: Record<string, number>;
  outcome_acted_pct: string | null;
  outcome_not_acted_pct: string | null;
  outcome_samples: number;
}

export const investmentPlanService = {
  getPlan: () => httpClient.get<PlanResponse>('/portfolio/plan'),
  savePlan: (enabled: boolean, plan: InvestmentPlan) =>
    httpClient.put<PlanResponse>('/portfolio/plan', { enabled, plan }),
  allocation: () => httpClient.get<AllocationResponse>('/portfolio/allocation'),
  check: (dryRun: boolean) => httpClient.post<Record<string, unknown>>('/portfolio/check', { dry_run: dryRun }),
  recommendations: (status: 'open' | 'history' | 'all' = 'open') =>
    httpClient.get<{ recommendations: Recommendation[]; disclaimer: string }>(
      `/portfolio/recommendations?status=${status}`),
  setAccountClass: (accountId: string, assetClass: AssetClass | '') =>
    httpClient.put<PlanResponse>(`/portfolio/plan/accounts/${accountId}`, { asset_class: assetClass }),
  risk: () => httpClient.get<{ configured: boolean; as_of?: string; risk?: RiskProfile; disclaimer: string }>(
    '/portfolio/risk', { timeout: 120000 }),
  setRiskTier: (overrides: { symbol: string; tier: RiskTier; reason?: string }[]) =>
    httpClient.put<{ overrides: { symbol: string; tier: RiskTier; reason?: string }[] }>('/portfolio/risk-tiers', { overrides }),
  riskTiers: () => httpClient.get<{ overrides: { symbol: string; tier: RiskTier; reason?: string }[] }>('/portfolio/risk-tiers'),
  reminders: () => httpClient.get<{ reminders: ReminderRule[]; kinds: string[] }>('/portfolio/reminders'),
  saveReminder: (r: ReminderRule) => (r.id
    ? httpClient.put<ReminderRule>(`/portfolio/reminders/${r.id}`, r)
    : httpClient.post<ReminderRule>('/portfolio/reminders', r)),
  deleteReminder: (id: string) => httpClient.delete<void>(`/portfolio/reminders/${id}`),
  testReminder: (id: string) => httpClient.post<{ result: ReminderResult; dry_run: boolean }>(`/portfolio/reminders/${id}/test`, {}),
  pauseStream: (id: string, paused: boolean) =>
    httpClient.post<PlanResponse>(`/portfolio/plan/streams/${encodeURIComponent(id)}/pause`, { paused }),
  dismiss: (id: string, reason: string) =>
    httpClient.post<{ id: string; status: string }>(`/portfolio/recommendations/${id}/dismiss`, { reason }),
  stats: () => httpClient.get<{ stats: RecStats; disclaimer: string }>('/portfolio/recommendations/stats'),
  signals: (days = 7) =>
    httpClient.get<{ signals: StoredSignal[]; disclaimer: string }>(`/portfolio/signals?days=${days}`),
};
